import { useState } from 'react';
import { Paperclip, TriangleAlert } from 'lucide-react';
import { ApiError } from '../../lib/apiClient';
import { getAttachmentUrl } from '../../lib/mailApi';
import { formatBytes } from '../../lib/mailFormat';
import type { MailAttachmentDto } from '../../lib/mailTypes';

export function MailAttachments({
  threadId,
  messageId,
  attachments,
}: {
  threadId: string;
  messageId: string;
  attachments: MailAttachmentDto[];
}) {
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (attachments.length === 0) return null;

  const open = async (att: MailAttachmentDto) => {
    if (
      att.risky &&
      !window.confirm(
        `"${att.filename}" may be unsafe (executable or archive). Only open it if you trust the sender. Open anyway?`,
      )
    ) {
      return;
    }
    setBusy(att.index);
    setError(null);
    try {
      const { url } = await getAttachmentUrl(threadId, messageId, att.index);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open the attachment');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mail-attachments">
      <ul>
        {attachments.map((att) => (
          <li key={att.index}>
            <button
              type="button"
              className={att.risky ? 'mail-attachment mail-attachment-risky' : 'mail-attachment'}
              disabled={busy === att.index}
              onClick={() => void open(att)}
              title={att.contentType}
            >
              {att.risky ? <TriangleAlert size={14} aria-hidden /> : <Paperclip size={14} aria-hidden />}
              <span className="mail-attachment-name">{att.filename}</span>
              <span className="cell-sub">{formatBytes(att.size)}</span>
              {att.risky && <span className="mail-attachment-warn">Potentially unsafe</span>}
            </button>
          </li>
        ))}
      </ul>
      {error && (
        <div role="alert" className="error-banner">
          {error}
        </div>
      )}
    </div>
  );
}
